import { useTrackActions } from "@/utils/hooks/useTracking";
import {
  createElementsOptions, getStripe, paymentElementOptions
} from "@/utils/stripe";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import {
  Alert, Autocomplete, Box, Card, Checkbox, CircularProgress, FormControlLabel, Radio,
  RadioGroup, styled, TextField, Typography
} from "@mui/material";
import {
  Elements,
  PaymentElement, useElements, useStripe
} from "@stripe/react-stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  autocompleteAddress,
  type CheckoutSessionAutocompleteResponse
} from "../../../../../../api/rest/checkoutSimplifiedApi";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";
import type { Address, SavedPaymentMethod } from "../../types";
import CollapsibleSection from "./CollapsibleSection";

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "0",
  paddingBottom: "8px",
});

const PaymentElementContainer = styled(Box)({
  marginBottom: "16px",
});

const BillingAddressContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "16px",
});

const FormRow = styled(Box)({
  display: "flex",
  gap: "16px",
  "@media (max-width: 768px)": {
    flexDirection: "column",
  },
});

const SavedPaymentMethodCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  marginBottom: "8px",
  border: "1px solid",
  borderColor: theme.palette.divider,
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  "&.selected": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
}));

const PaymentMethodOption = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px",
});

// US States for dropdown
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

interface PaymentFormProps {
  onSuccess: () => void;
  disabled?: boolean;
  savedPaymentMethods?: SavedPaymentMethod[];
}

const PaymentForm = ({ onSuccess, disabled = false, savedPaymentMethods = [] }: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { session, completeCheckout, setTriggerPaymentSubmission, contactHasErrors, shippingAddressHasErrors } =
    useCheckoutSimplifiedUI();
  const [trackAction] = useTrackActions();

  const [paymentMethodSelection, setPaymentMethodSelection] = useState<string>(
    savedPaymentMethods.length > 0 ? savedPaymentMethods[0].id : "new"
  );
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [billingFirstName, setBillingFirstName] = useState("");
  const [billingLastName, setBillingLastName] = useState("");
  const [billingAddress, setBillingAddress] = useState<Address>({
    name: "",
    line_one: "",
    line_two: "",
    city: "",
    state: "",
    country: "US",
    postal_code: "",
  });

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  const isUsingSavedPaymentMethod = paymentMethodSelection !== "new";

  // Refs to track when resetting via Escape key
  const isResettingNameRef = useRef(false);
  const isResettingFieldRef = useRef<Record<keyof Address, boolean>>({
    name: false,
    line_one: false,
    line_two: false,
    city: false,
    state: false,
    country: false,
    postal_code: false,
    phone_number: false,
  });

  // Billing address autocomplete state
  type AddressOption = NonNullable<
    CheckoutSessionAutocompleteResponse["autocompleteResult"]
  >[number];
  const [billingAddressOptions, setBillingAddressOptions] = useState<
    AddressOption[]
  >([]);
  const [billingAddressInputValue, setBillingAddressInputValue] = useState("");
  const [loadingBillingAddressOptions, setLoadingBillingAddressOptions] =
    useState(false);
  const billingAddressFetchTimeoutRef = useRef<NodeJS.Timeout>();
  const billingAddressInputRef = useRef<HTMLInputElement>(null);

  // Initialize billing address from shipping if same
  useEffect(() => {
    if (useSameAddress && session?.fulfillment_address) {
      setBillingAddress(session.fulfillment_address);
      if (session.fulfillment_address.line_one) {
        setBillingAddressInputValue(session.fulfillment_address.line_one);
      }

      // Split name into first and last name
      const nameParts = session.fulfillment_address.name.split(" ");
      if (nameParts.length > 1) {
        setBillingFirstName(nameParts[0]);
        setBillingLastName(nameParts.slice(1).join(" "));
      } else {
        setBillingFirstName(session.fulfillment_address.name || "");
        setBillingLastName("");
      }
    }
  }, [useSameAddress, session?.fulfillment_address]);

  // Expose submit function to context for external trigger
  useEffect(() => {
    const canSubmit = isFormValid();
    if (canSubmit) {
      setTriggerPaymentSubmission(() => async () => {
        // Trigger the form submission programmatically
        const syntheticEvent = { preventDefault: () => { } } as React.FormEvent;
        await handleSubmit(syntheticEvent);
      });
    } else {
      setTriggerPaymentSubmission(null);
    }
    return () => setTriggerPaymentSubmission(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stripe,
    elements,
    paymentReady,
    paymentComplete,
    session?.status,
    useSameAddress,
    billingFirstName,
    billingLastName,
    billingAddress,
    processing,
    disabled,
    selectedPaymentMethodId,
    paymentMethodSelection,
    isUsingSavedPaymentMethod,
    contactHasErrors,
    shippingAddressHasErrors,
  ]);

  // Fetch billing address autocomplete suggestions
  const fetchBillingAddressSuggestions = useCallback(
    async (searchText: string) => {
      if (!searchText || !session?.id) {
        setBillingAddressOptions([]);
        return;
      }

      try {
        setLoadingBillingAddressOptions(true);
        const response = await autocompleteAddress(session.id, {
          searchText,
          countryCodes: ["US"],
        });

        if (response.status === "success" && response.autocompleteResult) {
          setBillingAddressOptions(response.autocompleteResult);
        } else {
          setBillingAddressOptions([]);
        }
      } catch (error) {
        console.error("Failed to fetch billing address suggestions:", error);
        setBillingAddressOptions([]);
      } finally {
        setLoadingBillingAddressOptions(false);
      }
    },
    [session?.id]
  );

  // Parse billing address from terms and auto-fill fields
  const handleSelectBillingAddress = useCallback((option: AddressOption) => {
    // Parse address from terms array
    // Expected structure: [street_number, street_name, city, state, country]
    const terms = option.terms;

    if (!terms || terms.length < 3) {
      // Not enough information, just use main_text
      const address = option.structured_formatting.main_text;
      setBillingAddressInputValue(address);
      setBillingAddress((prev) => ({ ...prev, line_one: address }));
      return;
    }

    // Extract address components from terms
    let streetAddress = "";
    let city = "";
    let state = "";

    // Street address is typically the first 2 terms (number + street name)
    if (terms.length >= 2) {
      streetAddress = `${terms[0].value} ${terms[1].value}`.trim();
    } else if (terms.length >= 1) {
      streetAddress = terms[0].value;
    }

    // City is typically the 3rd term
    if (terms.length >= 3) {
      city = terms[2].value;
    }

    // State is typically the 4th term
    if (terms.length >= 4) {
      state = terms[3].value;
    }

    // Update form data
    setBillingAddressInputValue(streetAddress);
    setBillingAddress((prev) => ({
      ...prev,
      line_one: streetAddress,
      city: city,
      state: state,
    }));

    // Blur the street address field
    setTimeout(() => {
      billingAddressInputRef.current?.blur();
    }, 100);
  }, []);

  // Debounced billing address autocomplete
  useEffect(() => {
    if (billingAddressFetchTimeoutRef.current) {
      clearTimeout(billingAddressFetchTimeoutRef.current);
    }

    if (billingAddressInputValue.length >= 3) {
      billingAddressFetchTimeoutRef.current = setTimeout(() => {
        fetchBillingAddressSuggestions(billingAddressInputValue);
      }, 300);
    } else {
      setBillingAddressOptions([]);
    }

    return () => {
      if (billingAddressFetchTimeoutRef.current) {
        clearTimeout(billingAddressFetchTimeoutRef.current);
      }
    };
  }, [billingAddressInputValue, fetchBillingAddressSuggestions]);

  const handleAddressChange =
    (field: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setBillingAddress((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      // Revert to shipping address name
      if (session?.fulfillment_address?.name) {
        const nameParts = session.fulfillment_address.name.split(" ");
        if (nameParts.length > 1) {
          setBillingFirstName(nameParts[0]);
          setBillingLastName(nameParts.slice(1).join(" "));
        } else {
          setBillingFirstName(session.fulfillment_address.name);
          setBillingLastName("");
        }
      } else {
        setBillingFirstName("");
        setBillingLastName("");
      }

      // Set flag to skip any blur validation (for future-proofing)
      isResettingNameRef.current = true;

      (e.target as HTMLInputElement).blur();
    }
  };

  const handleFieldKeyDown =
    (field: keyof Address) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        // Revert to shipping address value
        const originalValue = session?.fulfillment_address?.[field] || "";
        setBillingAddress((prev) => ({ ...prev, [field]: originalValue }));

        // For line_one, also update the address input value
        if (field === "line_one") {
          setBillingAddressInputValue(originalValue);
        }

        // Set flag to skip any blur validation (for future-proofing)
        isResettingFieldRef.current[field] = true;

        (e.target as HTMLInputElement).blur();
      }
    };

  // Validate billing address if not using same address
  const isBillingAddressValid = (): boolean => {
    if (useSameAddress) {
      return true;
    }
    const fullName =
      `${billingFirstName.trim()} ${billingLastName.trim()}`.trim();
    return !!(
      fullName &&
      billingAddress.line_one &&
      billingAddress.city &&
      billingAddress.state &&
      billingAddress.postal_code
    );
  };

  // Check if form is ready to submit
  const isFormValid = (): boolean => {
    // Check if any section has validation errors
    if (contactHasErrors || shippingAddressHasErrors) {
      return false;
    }

    // If using a saved payment method, we don't need PaymentElement to be ready/complete
    if (isUsingSavedPaymentMethod) {
      return !!(
        stripe &&
        session?.status === "ready_for_payment" &&
        !processing &&
        !disabled
      );
    }

    // For new payment methods, we need PaymentElement to be ready and complete
    return !!(
      stripe &&
      elements &&
      paymentReady &&
      paymentComplete &&
      session?.status === "ready_for_payment" &&
      isBillingAddressValid() &&
      !processing &&
      !disabled
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !session) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      let paymentMethodId: string;
      let finalBillingAddress: Address | undefined;

      // If using a saved payment method, use its ID and billing address
      if (isUsingSavedPaymentMethod) {
        const savedMethod = savedPaymentMethods.find(
          (pm) => pm.id === paymentMethodSelection
        );

        if (!savedMethod) {
          setError("Selected payment method not found");
          trackAction?.(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
            sessionId: session.id,
            checkoutIntentId: session.id,
            step: "payment",
            errorMessage: "Selected payment method not found",
          });
          setProcessing(false);
          return;
        }

        paymentMethodId = savedMethod.id;
        finalBillingAddress = savedMethod.billing_details.address;
      } else {
        // Creating a new payment method
        if (!elements) {
          setError("Payment form not loaded");
          trackAction?.(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
            sessionId: session.id,
            checkoutIntentId: session.id,
            step: "payment",
            errorMessage: "Payment form not loaded",
          });
          setProcessing(false);
          return;
        }

        // Submit payment element to validate
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || "Payment validation failed");
          trackAction?.(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
            sessionId: session.id,
            checkoutIntentId: session.id,
            step: "payment",
            errorMessage: submitError.message || "Payment validation failed",
          });
          setProcessing(false);
          return;
        }

        // Prepare billing details
        finalBillingAddress = useSameAddress
          ? session.fulfillment_address
          : {
            ...billingAddress,
            name: `${billingFirstName.trim()} ${billingLastName.trim()}`.trim(),
          };

        // Create payment method with billing details
        const { error: paymentMethodError, paymentMethod } =
          await stripe.createPaymentMethod({
            elements,
            params: {
              billing_details: finalBillingAddress
                ? {
                  name: finalBillingAddress.name,
                  address: {
                    line1: finalBillingAddress.line_one,
                    line2: finalBillingAddress.line_two || undefined,
                    city: finalBillingAddress.city,
                    state: finalBillingAddress.state,
                    country: finalBillingAddress.country,
                    postal_code: finalBillingAddress.postal_code,
                  },
                }
                : undefined,
            },
          });

        if (paymentMethodError) {
          setError(
            paymentMethodError.message || "Failed to create payment method"
          );
          trackAction?.(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
            sessionId: session.id,
            checkoutIntentId: session.id,
            step: "payment",
            errorMessage: paymentMethodError.message || "Failed to create payment method",
          });
          setProcessing(false);
          return;
        }

        if (!paymentMethod?.id) {
          setError("Failed to create payment method");
          trackAction?.(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
            sessionId: session.id,
            checkoutIntentId: session.id,
            step: "payment",
            errorMessage: "No payment method ID returned",
          });
          setProcessing(false);
          return;
        }

        paymentMethodId = paymentMethod.id;
      }

      // Store the payment method ID
      setSelectedPaymentMethodId(paymentMethodId);

      // Complete checkout on backend with payment method ID
      await completeCheckout(paymentMethodId, finalBillingAddress);

      onSuccess();
    } catch (err: any) {
      console.error("Payment error:", err);
      setError(err.message || "Payment failed. Please try again.");

      // Track order failure
      trackAction?.(AnalyticsTrackingEvent.CHECKOUT_ORDER_FAILURE, {
        sessionId: session.id,
        checkoutIntentId: session.id,
        checkoutPhase: "submission_error",
        errorMessage: err.message || "Payment failed",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Helper function to format card brand
  const formatCardBrand = (brand: string): string => {
    const brandMap: Record<string, string> = {
      amex: "American Express",
      visa: "Visa",
      mastercard: "Mastercard",
      discover: "Discover",
    };
    return brandMap[brand.toLowerCase()] || brand;
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormContainer>
        {savedPaymentMethods.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Payment Method
            </Typography>
            <RadioGroup
              value={paymentMethodSelection}
              onChange={(e) => setPaymentMethodSelection(e.target.value)}
            >
              {savedPaymentMethods.map((method) => (
                <SavedPaymentMethodCard
                  key={method.id}
                  className={paymentMethodSelection === method.id ? "selected" : ""}
                  onClick={() => setPaymentMethodSelection(method.id)}
                >
                  <PaymentMethodOption>
                    <Radio
                      value={method.id}
                      disabled={disabled || processing}
                      checked={paymentMethodSelection === method.id}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {formatCardBrand(method.card.brand)} •••• {method.card.last4}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Expires {method.card.exp_month}/{method.card.exp_year}
                        {method.billing_details.name && ` • ${method.billing_details.name}`}
                      </Typography>
                    </Box>
                  </PaymentMethodOption>
                </SavedPaymentMethodCard>
              ))}
              <SavedPaymentMethodCard
                className={paymentMethodSelection === "new" ? "selected" : ""}
                onClick={() => setPaymentMethodSelection("new")}
              >
                <PaymentMethodOption>
                  <Radio
                    value="new"
                    disabled={disabled || processing}
                    checked={paymentMethodSelection === "new"}
                  />
                  <Typography variant="body1" fontWeight={500}>
                    Use a new payment method
                  </Typography>
                </PaymentMethodOption>
              </SavedPaymentMethodCard>
            </RadioGroup>
          </Box>
        )}

        {paymentMethodSelection === "new" && (
          <PaymentElementContainer>
            <PaymentElement
              options={paymentElementOptions}
              onReady={() => setPaymentReady(true)}
              onChange={(e) => {
                console.log("PaymentElement onChange", e);
                // Update payment complete status when user enters/changes payment details
                setPaymentComplete(e.complete);
                // Clear any previous errors when user changes payment info
                if (error) {
                  setError(null);
                }
              }}
            />
          </PaymentElementContainer>
        )}

        {paymentMethodSelection === "new" && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useSameAddress}
                  onChange={(e) => setUseSameAddress(e.target.checked)}
                  disabled={disabled || processing}
                />
              }
              label="Billing address same as shipping"
            />

            {!useSameAddress && (
              <BillingAddressContainer>
                <FormRow>
                  <TextField
                    fullWidth
                    label="First name"
                    value={billingFirstName}
                    onChange={(e) => setBillingFirstName(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    disabled={disabled || processing}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Last name"
                    value={billingLastName}
                    onChange={(e) => setBillingLastName(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    disabled={disabled || processing}
                    required
                  />
                </FormRow>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={billingAddressOptions}
                  loading={loadingBillingAddressOptions}
                  inputValue={billingAddressInputValue}
                  onInputChange={(_, newValue, reason) => {
                    // Only update local input state while typing, don't update formData
                    if (reason === "input") {
                      setBillingAddressInputValue(newValue);
                    }
                  }}
                  onChange={(_, newValue) => {
                    // Only update formData when user selects from dropdown
                    if (typeof newValue === "string") {
                      setBillingAddressInputValue(newValue);
                      setBillingAddress((prev) => ({ ...prev, line_one: newValue }));
                    } else if (newValue) {
                      // User selected from dropdown - parse and auto-fill
                      handleSelectBillingAddress(newValue);
                    }
                  }}
                  onBlur={() => {
                    // Update formData on blur
                    setBillingAddress((prev) => ({
                      ...prev,
                      line_one: billingAddressInputValue,
                    }));
                  }}
                  disabled={disabled || processing}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    // Return only the main text, not the full description
                    return option.structured_formatting.main_text;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (typeof option === "string" && typeof value === "string") {
                      return option === value;
                    }
                    if (typeof option === "object" && typeof value === "object") {
                      return option.place_id === value.place_id;
                    }
                    return false;
                  }}
                  renderOption={(props, option) => {
                    // Use place_id as unique key instead of the default behavior
                    const { key, ...otherProps } = props;
                    return (
                      <li key={option.place_id} {...otherProps}>
                        <Box>
                          <Box sx={{ fontWeight: 500 }}>
                            {option.structured_formatting.main_text}
                          </Box>
                          <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                            {option.structured_formatting.secondary_text}
                          </Box>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Street address"
                      required
                      inputRef={billingAddressInputRef}
                      onKeyDown={handleFieldKeyDown("line_one")}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingBillingAddressOptions ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <TextField
                  fullWidth
                  label="Apartment, suite, etc. (optional)"
                  value={billingAddress.line_two}
                  onChange={handleAddressChange("line_two")}
                  onKeyDown={handleFieldKeyDown("line_two")}
                  disabled={disabled || processing}
                />
                <FormRow>
                  <TextField
                    fullWidth
                    label="City"
                    value={billingAddress.city}
                    onChange={handleAddressChange("city")}
                    onKeyDown={handleFieldKeyDown("city")}
                    disabled={disabled || processing}
                    required
                  />
                  <Autocomplete
                    fullWidth
                    options={US_STATES}
                    getOptionLabel={(option) => option.name}
                    value={
                      US_STATES.find((s) => s.code === billingAddress.state) || null
                    }
                    onChange={(_, newValue) => {
                      const value = newValue?.code || "";
                      setBillingAddress((prev) => ({ ...prev, state: value }));
                    }}
                    disabled={disabled || processing}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        required
                        onKeyDown={handleFieldKeyDown("state")}
                      />
                    )}
                  />
                </FormRow>
                <TextField
                  fullWidth
                  label="ZIP code"
                  value={billingAddress.postal_code}
                  onChange={handleAddressChange("postal_code")}
                  onKeyDown={handleFieldKeyDown("postal_code")}
                  disabled={disabled || processing}
                  required
                />
              </BillingAddressContainer>
            )}
          </>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {processing && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
          >
            <CircularProgress size={24} />
            <Typography ml={2}>Processing payment...</Typography>
          </Box>
        )}
      </FormContainer>
    </form>
  );
};

interface PaymentSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  onSuccess: () => void;
  disabled?: boolean;
}

export const PaymentSection = ({
  isOpen,
  onToggle,
  onSuccess,
  disabled = false,
}: PaymentSectionProps) => {
  const { session } = useCheckoutSimplifiedUI();
  const [stripePromise] = useState(() => getStripe());

  // Use the client_secret from the payment_intent if available
  const clientSecret = session?.payment_intent?.client_secret;
  const elementsOptions = createElementsOptions(clientSecret);
  const savedPaymentMethods = session?.saved_payment_methods || [];

  // Don't render if no payment intent yet
  if (!clientSecret) {
    return (
      <CollapsibleSection
        isOpen={isOpen}
        onToggle={onToggle}
        disabled={disabled}
        loading={false}
        title="Payment"
        hideControl={false}
      >
        <Box sx={{ padding: 2, textAlign: "center", color: "text.secondary" }}>
          Waiting for payment setup...
        </Box>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={disabled}
      loading={false}
      title="Payment"
      hideControl={false}
    >
      <Elements
        stripe={stripePromise}
        options={elementsOptions}
        key={clientSecret}
      >
        <PaymentForm
          onSuccess={onSuccess}
          disabled={disabled}
          savedPaymentMethods={savedPaymentMethods}
        />
      </Elements>
    </CollapsibleSection>
  );
};
